/**
 * WorkOrderScheduleBar — the work order's schedule, directly under the header
 * row (#2105).
 *
 * Replaces the schedule half of the old WorkOrderEditModal, which hid behind
 * the customer card's pencil: schedule facts belong at the top of the work
 * order, and they are edited in place rather than in a dialog.
 *
 * Read-only rows (work started, quote validity, the mechanic's queue slot) are
 * derived backend-side and only ever displayed.
 *
 * A quote is the exception (#2647): its stored time is a technical default,
 * so the bar shows no time, duration, mechanic or pickup for it and offers
 * no pencil. Instead it shows the validity and the one action that gives a
 * quote a real schedule — "Accept and schedule", which opens
 * AcceptQuoteScheduleDialog. Editing a quote's time here used to write a
 * second schedule the accept dialog then ignored.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import { format, parseISO } from 'date-fns';
import { DetailField } from '@/components/common';
import { useUpdateBooking } from '@/hooks/useBookings';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';
import { useNotify } from '@/stores/notificationStore';
import { formatDuration, parseDurationHours, sumHours } from '@/utils/duration';
import { workOrderBillableHours } from '@/utils/actualTime';
import { WorkOrderDurationTrio } from './WorkOrderDurationTrio';
import { WorkOrderMechanicsSummary } from './WorkOrderMechanicsSummary';
import {
  floatingCivilDateFromStored,
  floatingFormDateTime,
  toDateTimeLocal,
} from './WorkOrderCreateModal';
import { floatingScheduledAtIso } from './create/requestTimes';
import { dateFnsLocale } from '@/utils/locale';

interface MechanicOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface WorkOrderScheduleBarProps {
  workOrder: WorkOrderDetail;
  mechanics: MechanicOption[];
  /** Opens straight in edit mode — the kanban "edit" action (`?edit=true`). */
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** Opens the accept-and-schedule dialog; the only schedule action for a quote (#2647). */
  onAcceptQuote?: (() => void) | undefined;
  /** Opens the send-quote dialog (#2651). */
  onSendQuote?: (() => void) | undefined;
}

type WorkOrderService = WorkOrderDetail['services'][number];

/** Draft of one service's planned hours while the schedule bar is in edit mode. */
interface ServicePlannedDraft {
  bookingServiceId: string;
  name: string;
  input: string;
  /** Parsed hours; `null` means restore-to-derived. `''` is empty/invalid. */
  hours: number | '' | null;
  originalHours: number | null;
  originalManual: boolean;
  manual: boolean;
  guidanceHours: number | null;
}

function guidanceHoursForService(service: WorkOrderService): number | null {
  const items = service.laborItems ?? [];
  if (items.length === 0) return null;
  return sumHours(items.map((item) => item.estimatedHours));
}

function seedServiceDrafts(services: WorkOrderService[]): ServicePlannedDraft[] {
  return services.map((service) => {
    const hours = service.estimatedDurationHours ?? null;
    return {
      bookingServiceId: service.bookingServiceId,
      name: service.name,
      input: hours != null ? String(hours) : '',
      hours: hours ?? '',
      originalHours: hours,
      originalManual: service.plannedHoursManual === true,
      manual: service.plannedHoursManual === true,
      guidanceHours: guidanceHoursForService(service),
    };
  });
}

function hasInvalidManualDuration(draft: ServicePlannedDraft): boolean {
  return draft.hours === '' && draft.manual;
}

function changedServiceSchedules(
  drafts: ServicePlannedDraft[],
): Record<string, { estimatedDurationHours: number | null }> | undefined {
  const result: Record<string, { estimatedDurationHours: number | null }> = {};
  for (const draft of drafts) {
    if (draft.hours === null) {
      if (draft.originalManual) {
        result[draft.bookingServiceId] = { estimatedDurationHours: null };
      }
      continue;
    }
    if (typeof draft.hours !== 'number') continue;
    // Typing the same hours as a derived value is still an explicit latch:
    // the backend sets plannedHoursManual from a non-null duration (#2667).
    const latchedManual = draft.manual && !draft.originalManual;
    if (draft.hours !== draft.originalHours || latchedManual) {
      result[draft.bookingServiceId] = { estimatedDurationHours: draft.hours };
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function formatDateTime(iso: string): string {
  return format(parseISO(iso), 'd. MMMM yyyy HH:mm', { locale: dateFnsLocale() });
}

type QuoteValidityDisplay =
  | { kind: 'date'; iso: string }
  | { kind: 'days'; days: number }
  | { kind: 'none' };

/** Days-quotes ignore the raw column until the first send starts the clock (#4120). */
function describeQuoteValidity(workOrder: WorkOrderDetail): QuoteValidityDisplay {
  if (workOrder.quoteValidityDays != null) {
    if (workOrder.effectiveQuoteExpiresAt) return { kind: 'date', iso: workOrder.effectiveQuoteExpiresAt };
    return { kind: 'days', days: workOrder.quoteValidityDays };
  }
  const iso = workOrder.effectiveQuoteExpiresAt ?? workOrder.quoteExpiresAt;
  if (iso) return { kind: 'date', iso };
  return { kind: 'none' };
}

export function WorkOrderScheduleBar({
  workOrder,
  mechanics,
  editing,
  onEditingChange,
  onDirtyChange,
  onAcceptQuote,
  onSendQuote,
}: WorkOrderScheduleBarProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const mechanicMru = useDropdownMru(DROPDOWN_MRU_SELECTORS.mechanic);
  const updateBooking = useUpdateBooking();

  const [schedulingType, setSchedulingType] = useState<'exact' | 'floating'>('exact');
  const [scheduledAt, setScheduledAt] = useState('');
  const [vehicleDropoffAt, setVehicleDropoffAt] = useState('');
  const [customerRequestedPickupAt, setCustomerRequestedPickupAt] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState<number | ''>('');
  // Free-text input kept separate from the numeric state so a partial Finnish
  // value ("1,") does not fight the cursor while typing.
  const [estimatedDurationInput, setEstimatedDurationInput] = useState('');
  const [serviceDrafts, setServiceDrafts] = useState<ServicePlannedDraft[]>([]);
  const [mechanicId, setMechanicId] = useState('');
  const [customerWaits, setCustomerWaits] = useState(false);
  const [allowMinorMaintenance, setAllowMinorMaintenance] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Seed the form once per edit session, so a cancelled edit never leaves a
  // stale draft behind — and so a background refetch of the work order while
  // the form is open cannot overwrite what the user is typing.
  const seeded = useRef(false);
  useEffect(() => {
    if (!editing) {
      seeded.current = false;
      return;
    }
    if (seeded.current) return;
    seeded.current = true;
    const type = workOrder.schedulingType ?? 'exact';
    const scheduledLocal =
      type === 'floating'
        ? floatingFormDateTime(floatingCivilDateFromStored(workOrder.scheduledAt))
        : toDateTimeLocal(new Date(workOrder.scheduledAt));
    setSchedulingType(type);
    setScheduledAt(scheduledLocal);
    setVehicleDropoffAt(workOrder.vehicleDropoffAt ? toDateTimeLocal(new Date(workOrder.vehicleDropoffAt)) : '');
    setCustomerRequestedPickupAt(
      workOrder.customerRequestedPickupAt
        ? toDateTimeLocal(new Date(workOrder.customerRequestedPickupAt))
        : scheduledLocal,
    );
    setEstimatedDuration(workOrder.estimatedDurationHours ?? '');
    setEstimatedDurationInput(
      workOrder.estimatedDurationHours != null ? String(workOrder.estimatedDurationHours) : '',
    );
    setServiceDrafts(seedServiceDrafts(workOrder.services ?? []));
    setMechanicId(workOrder.assignedMechanicId ?? '');
    setCustomerWaits(workOrder.customerWaits ?? false);
    setAllowMinorMaintenance(workOrder.allowMinorMaintenance ?? false);
    setIsDirty(false);
  }, [editing, workOrder]);

  useEffect(() => {
    onDirtyChange?.(editing && isDirty);
  }, [editing, isDirty, onDirtyChange]);

  const hasServices = (workOrder.services?.length ?? 0) > 0;

  const livePlannedTotal = useMemo(() => {
    if (!hasServices) {
      return typeof estimatedDuration === 'number' ? estimatedDuration : null;
    }
    return sumHours(
      serviceDrafts.map((draft) => {
        if (draft.hours === null) return draft.guidanceHours;
        if (draft.hours === '') return null;
        return draft.hours;
      }),
    );
  }, [hasServices, estimatedDuration, serviceDrafts]);

  const mechanicName = useMemo(() => {
    const assigned = mechanics.find((m) => m.id === workOrder.assignedMechanicId);
    if (assigned) return `${assigned.firstName} ${assigned.lastName}`.trim();
    if (workOrder.mechanic) {
      return [workOrder.mechanic.firstName, workOrder.mechanic.lastName].filter(Boolean).join(' ');
    }
    return '';
  }, [mechanics, workOrder.assignedMechanicId, workOrder.mechanic]);

  const hasInvalidServiceDuration = serviceDrafts.some(hasInvalidManualDuration);

  const handleSave = async () => {
    if (hasServices && hasInvalidServiceDuration) return;
    setIsSaving(true);
    try {
      const serviceSchedules = hasServices ? changedServiceSchedules(serviceDrafts) : undefined;
      await updateBooking.mutateAsync({
        id: workOrder.id,
        data: {
          // A floating booking carries only the day: UTC midnight of that civil
          // date (#1965). Same encoding as WorkOrderCreateModal.
          scheduledAt:
            schedulingType === 'floating'
              ? floatingScheduledAtIso(scheduledAt)
              : parseISO(scheduledAt).toISOString(),
          schedulingType,
          vehicleDropoffAt: vehicleDropoffAt ? parseISO(vehicleDropoffAt).toISOString() : null,
          // The pickup field mirrors scheduledAt until touched — for a floating
          // booking that mirror is a meaningless midnight, so it isn't sent.
          customerRequestedPickupAt:
            customerRequestedPickupAt
            && !(schedulingType === 'floating' && customerRequestedPickupAt === scheduledAt)
              ? parseISO(customerRequestedPickupAt).toISOString()
              : null,
          // Service-backed bookings never send overall estimatedDurationHours;
          // planned time is patched per service (#2667).
          ...(hasServices
            ? (serviceSchedules ? { serviceSchedules } : {})
            : (estimatedDuration === '' ? {} : { estimatedDurationHours: estimatedDuration })),
          assignedMechanicId: mechanicId || null,
          customerWaits,
          allowMinorMaintenance,
        },
      });
      notify.success(t('editSuccess'), t('editSuccessDesc'));
      setIsDirty(false);
      onEditingChange(false);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('editFailed') }));
    } finally {
      setIsSaving(false);
    }
  };

  const updateServiceHours = (bookingServiceId: string, raw: string) => {
    setServiceDrafts((prev) =>
      prev.map((draft) => {
        if (draft.bookingServiceId !== bookingServiceId) return draft;
        const parsed = parseDurationHours(raw);
        return {
          ...draft,
          input: raw,
          hours: parsed ?? '',
          manual: true,
        };
      }),
    );
    setIsDirty(true);
  };

  const restoreServiceGuidance = (bookingServiceId: string) => {
    setServiceDrafts((prev) =>
      prev.map((draft) => {
        if (draft.bookingServiceId !== bookingServiceId) return draft;
        const preview = draft.guidanceHours;
        const restoredInput =
          preview != null
            ? String(preview)
            : draft.originalManual
              ? ''
              : (draft.originalHours != null ? String(draft.originalHours) : '');
        return {
          ...draft,
          manual: false,
          hours: draft.originalManual ? null : (draft.originalHours ?? ''),
          input: restoredInput,
        };
      }),
    );
    setIsDirty(true);
  };

  const isQuote = workOrder.status === 'quote';
  const quoteValidity = isQuote ? describeQuoteValidity(workOrder) : { kind: 'none' as const };
  const quoteExpired = quoteValidity.kind === 'date' && new Date(quoteValidity.iso) < new Date();
  const hasChips = !!(workOrder.customerWaits || workOrder.allowMinorMaintenance || quoteExpired);

  if (isQuote) {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          mb: 3,
        }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('detail.scheduleTitle')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {quoteValidity.kind === 'date' && (
              <DetailField
                label={t('detail.quoteValidUntil')}
                value={format(parseISO(quoteValidity.iso), 'd. MMMM yyyy', { locale: dateFnsLocale() })}
              />
            )}
            {quoteValidity.kind === 'days' && (
              <DetailField
                label={t('detail.quoteValidUntil')}
                value={t('detail.quoteValidForDaysFromSend', { count: quoteValidity.days })}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {t('detail.quoteScheduleHint')}
            </Typography>
            {/* Send-out state (#2651): waiting, accepted or declined by the customer. */}
            {workOrder.quoteSend && (
              <Typography
                variant="body2"
                color={workOrder.quoteSend.response === 'rejected' ? 'error.main' : 'text.primary'}
              >
                {workOrder.quoteSend.response === 'accepted'
                  ? t('sendQuote.statusAccepted', {
                      date: format(parseISO(workOrder.quoteSend.respondedAt ?? workOrder.quoteSend.sentAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() }),
                    })
                  : workOrder.quoteSend.response === 'partial'
                    ? t('sendQuote.statusPartial', {
                        date: format(parseISO(workOrder.quoteSend.respondedAt ?? workOrder.quoteSend.sentAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() }),
                      })
                    : workOrder.quoteSend.response === 'rejected'
                      ? t('sendQuote.statusRejected', {
                          date: format(parseISO(workOrder.quoteSend.respondedAt ?? workOrder.quoteSend.sentAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() }),
                          reason: workOrder.quoteSend.rejectionReason ?? '',
                        })
                      : t('sendQuote.statusSent', {
                          date: format(parseISO(workOrder.quoteSend.sentAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() }),
                          channel: t(workOrder.quoteSend.sentVia === 'whatsapp' ? 'sendQuote.channelWhatsapp' : 'sendQuote.channelEmail'),
                          recipient: workOrder.quoteSend.recipient,
                        })}
                {workOrder.quoteSend.customerComment ? ` — ”${workOrder.quoteSend.customerComment}”` : ''}
              </Typography>
            )}
            {hasChips && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {quoteExpired && (
                  <Chip label={t('detail.quoteExpired')} color="error" size="small" />
                )}
                {workOrder.customerWaits && (
                  <Chip label={t('create.customerWaits')} color="info" size="small" />
                )}
                {workOrder.allowMinorMaintenance && (
                  <Chip label={t('create.allowMinorMaintenance')} color="success" size="small" />
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {onSendQuote && (
              <Button variant="outlined" onClick={onSendQuote} sx={{ whiteSpace: 'nowrap' }}>
                {workOrder.quoteSend ? t('sendQuote.sendAgain') : t('sendQuote.send')}
              </Button>
            )}
            {onAcceptQuote && (
              <Button variant="contained" onClick={onAcceptQuote} sx={{ whiteSpace: 'nowrap' }}>
                {t('acceptQuote.acceptAndSchedule')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.5, sm: 2 },
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="overline" color="text.secondary">
          {t('detail.scheduleTitle')}
        </Typography>
        {!editing && (
          <Tooltip title={t('detail.editSchedule')}>
            <IconButton
              size="small"
              onClick={() => { onEditingChange(true); }}
              aria-label={t('detail.editSchedule')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {editing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Exact time vs during-the-day (#1138) */}
          <ToggleButtonGroup
            value={schedulingType}
            exclusive
            onChange={(_event, value: 'exact' | 'floating' | null) => {
              if (!value || value === schedulingType) return;
              setSchedulingType(value);
              const day = scheduledAt.split('T')[0];
              if (day) {
                setScheduledAt(value === 'floating' ? floatingFormDateTime(day) : `${day}T08:00`);
              }
              setIsDirty(true);
            }}
            size="small"
            aria-label={t('create.schedulingTypeLabel')}
          >
            {/* Same order as creation (#1931). */}
            <ToggleButton value="floating">{t('create.schedulingTypeFloating')}</ToggleButton>
            <ToggleButton value="exact">{t('create.schedulingTypeExact')}</ToggleButton>
          </ToggleButtonGroup>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {/* 15-minute granularity matches what the scheduling engine supports.
                A floating booking has no time of day, only the date (#1138). */}
            {schedulingType === 'floating' ? (
              <TextField
                type="date"
                size="small"
                label={t('create.scheduledDay')}
                helperText={t('create.schedulingTypeFloatingHint')}
                value={scheduledAt.split('T')[0] ?? ''}
                onChange={(event) => {
                  setScheduledAt(floatingFormDateTime(event.target.value));
                  setIsDirty(true);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            ) : (
              <TextField
                type="datetime-local"
                size="small"
                label={t('create.scheduledTime')}
                value={scheduledAt}
                onChange={(event) => { setScheduledAt(event.target.value); setIsDirty(true); }}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { step: 900 },
                }}
              />
            )}

            <TextField
              type="datetime-local"
              size="small"
              label={t('create.vehicleDropoffAt')}
              helperText={t('create.vehicleDropoffAtHint')}
              value={vehicleDropoffAt}
              onChange={(event) => { setVehicleDropoffAt(event.target.value); setIsDirty(true); }}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 900 } }}
            />

            {/* Customer-requested pickup timestamp (#529 — was date-only #271) */}
            <TextField
              type="datetime-local"
              size="small"
              label={t('create.requestedPickupAt')}
              helperText={t('create.requestedPickupAtHint')}
              value={customerRequestedPickupAt}
              onChange={(event) => {
                setCustomerRequestedPickupAt(event.target.value);
                setIsDirty(true);
              }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: scheduledAt, step: 900 },
              }}
            />

            {!hasServices && (
              <TextField
                type="text"
                size="small"
                label={t('detail.plannedHours')}
                helperText={t('create.estimatedDurationHint')}
                value={estimatedDurationInput}
                onChange={(event) => {
                  const raw = event.target.value;
                  setEstimatedDurationInput(raw);
                  setEstimatedDuration(parseDurationHours(raw) ?? '');
                  setIsDirty(true);
                }}
                slotProps={{
                  input: { endAdornment: <InputAdornment position="end">h</InputAdornment> },
                  htmlInput: { inputMode: 'decimal', 'aria-label': t('detail.plannedHours') },
                }}
              />
            )}

            <Box>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('mechanic')}</InputLabel>
                <Select
                  value={mechanicId}
                  label={t('mechanic')}
                  onChange={(event) => {
                    const next = event.target.value;
                    setMechanicId(next);
                    setIsDirty(true);
                    if (next !== '') mechanicMru.record(next);
                  }}
                >
                  <MenuItem value="">{t('create.notAssigned')}</MenuItem>
                  {mechanicMru.order(mechanics, (mechanic) => mechanic.id).map((mechanic) => (
                    <MenuItem key={mechanic.id} value={mechanic.id}>
                      {mechanic.firstName} {mechanic.lastName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('scheduleBar.mechanicCascadeHint')}</FormHelperText>
              </FormControl>
              <WorkOrderMechanicsSummary
                services={workOrder.services ?? []}
                mechanics={mechanics}
              />
            </Box>
          </Box>

          {hasServices && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('detail.plannedHours')}: {formatDuration(livePlannedTotal)}
              </Typography>
              {serviceDrafts.map((draft) => {
                const fieldLabel = t('detail.servicePlannedHours', { name: draft.name });
                const invalid = hasInvalidManualDuration(draft);
                return (
                  <TextField
                    key={draft.bookingServiceId}
                    type="text"
                    size="small"
                    label={fieldLabel}
                    error={invalid}
                    helperText={
                      invalid
                        ? (draft.input.trim() === ''
                          ? tc('validation.required')
                          : t('create.estimatedDurationHint'))
                        : draft.manual
                          ? t('detail.plannedHoursManual')
                          : t('detail.plannedHoursDerived')
                    }
                    value={draft.input}
                    onChange={(event) => {
                      updateServiceHours(draft.bookingServiceId, event.target.value);
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <>
                            <InputAdornment position="end">h</InputAdornment>
                            {draft.manual && (
                              <InputAdornment position="end">
                                <Tooltip title={t('detail.restoreGuidanceTime')}>
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() => {
                                      restoreServiceGuidance(draft.bookingServiceId);
                                    }}
                                    aria-label={t('detail.restoreGuidanceTime')}
                                  >
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            )}
                          </>
                        ),
                      },
                      htmlInput: {
                        inputMode: 'decimal',
                        'aria-label': fieldLabel,
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={customerWaits}
                  onChange={(event) => { setCustomerWaits(event.target.checked); setIsDirty(true); }}
                />
              }
              label={t('create.customerWaits')}
            />
            <Tooltip title={t('create.allowMinorMaintenanceDesc')} placement="right" arrow>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowMinorMaintenance}
                    onChange={(event) => {
                      setAllowMinorMaintenance(event.target.checked);
                      setIsDirty(true);
                    }}
                  />
                }
                label={t('create.allowMinorMaintenance')}
              />
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => { onEditingChange(false); }} disabled={isSaving}>
              {tc('cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => { void handleSave(); }}
              disabled={isSaving || hasInvalidServiceDuration}
            >
              {isSaving ? <CircularProgress size={18} /> : tc('save')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <DetailField
              label={t('detail.scheduledDate')}
              value={
                workOrder.schedulingType === 'floating'
                  ? t('detail.scheduledDuringDay', {
                      date: format(
                        parseISO(floatingCivilDateFromStored(workOrder.scheduledAt)),
                        'd. MMMM yyyy',
                        { locale: dateFnsLocale() },
                      ),
                    })
                  : formatDateTime(workOrder.scheduledAt)
              }
            />
            <WorkOrderDurationTrio
              layout="fields"
              plannedHours={workOrder.estimatedDurationHours}
              billableHours={workOrderBillableHours(workOrder.services)}
              actual={workOrder}
              unassigned={workOrder}
            />
            <DetailField
              label={t('create.vehicleDropoffAt')}
              value={workOrder.vehicleDropoffAt ? formatDateTime(workOrder.vehicleDropoffAt) : null}
            />
            <Box>
              <DetailField
                label={t('mechanic')}
                value={mechanicName}
                placeholder={t('create.notAssigned')}
              />
              <WorkOrderMechanicsSummary
                services={workOrder.services ?? []}
                mechanics={mechanics}
              />
            </Box>
            <DetailField
              label={t('detail.requestedPickupAt')}
              value={
                workOrder.customerRequestedPickupAt
                && workOrder.customerRequestedPickupAt !== workOrder.scheduledAt
                  ? formatDateTime(workOrder.customerRequestedPickupAt)
                  : null
              }
            />
            {workOrder.mechanicScheduledStart && (
              <DetailField
                label={t('mechanicScheduledRange')}
                value={`${format(parseISO(workOrder.mechanicScheduledStart), 'd.M. HH:mm', { locale: dateFnsLocale() })}${
                  workOrder.mechanicScheduledEnd
                    ? ` – ${format(parseISO(workOrder.mechanicScheduledEnd), 'HH:mm', { locale: dateFnsLocale() })}`
                    : ''
                }`}
              />
            )}
            {workOrder.startedAt && (
              <DetailField
                label={t('detail.workStartedAt')}
                value={formatDateTime(workOrder.startedAt)}
              />
            )}
          </Box>

          {hasChips && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quoteExpired && (
                <Chip label={t('detail.quoteExpired')} color="error" size="small" />
              )}
              {workOrder.customerWaits && (
                <Chip label={t('create.customerWaits')} color="info" size="small" />
              )}
              {workOrder.allowMinorMaintenance && (
                <Chip label={t('create.allowMinorMaintenance')} color="success" size="small" />
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
